import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2 } from 'lucide-react';

interface AdminGuardProps {
  children: React.ReactNode;
  requireSuperAdmin?: boolean;
}

const AdminGuard: React.FC<AdminGuardProps> = ({ children, requireSuperAdmin = false }) => {
  const { user, loading } = useAuth();
  const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null);

  useEffect(() => {
    let isMounted = true;

    const checkAdminStatus = async () => {
      if (!user) {
        if (isMounted) setIsAuthorized(false);
        return;
      }

      try {
        // Force refresh token to get latest claims if they were just updated
        const tokenResult = await user.getIdTokenResult();
        const role = tokenResult.claims.role as string | undefined;

        if (requireSuperAdmin) {
          setIsAuthorized(role === 'superadmin');
        } else {
          setIsAuthorized(role === 'admin' || role === 'superadmin');
        }
      } catch (error) {
        console.error("Error checking admin claims:", error);
        if (isMounted) setIsAuthorized(false);
      }
    };

    if (!loading) {
      checkAdminStatus();
    }

    return () => {
      isMounted = false;
    };
  }, [user, loading, requireSuperAdmin]);

  if (loading || isAuthorized === null) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-4">
        <Loader2 className="h-10 w-10 animate-spin text-purple-600" />
        <p className="text-muted-foreground animate-pulse font-medium">Verificando credenciales de administrador...</p>
      </div>
    );
  }

  if (!user || !isAuthorized) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

export default AdminGuard;
